import Api from "../../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../../core/data/redux/apis/urls";

export type SwfOption = { code: string; description: string };

export const SWF_STATUS_FILTER_OPTIONS = [
  { key: "", labelKey: "all", label: "All" },
  { key: "pending", labelKey: "pending_draft", label: "Pending Draft" },
  { key: "approved", labelKey: "approved", label: "Approved" },
  { key: "rejected", labelKey: "rejected", label: "Rejected" },
  { key: "in process", labelKey: "in_process", label: "In Process" },
];

const STATUS_MAP: Record<string, string> = {
  N: "new",
  T: "to_be_approved",
  P: "partially_approved",
  A: "approved",
  R: "rejected",
  D: "processing_complete",
  X: "deleted",
  B: "to_be_processed",
  C: "closed",
  K: "cancelled",
  S: "settled",
};

const STATUS_LABELS: Record<string, string> = {
  N: "New",
  T: "To Be Approved",
  P: "Partially Approved",
  A: "Approved",
  R: "Rejected",
  D: "Processing Complete",
  X: "Deleted",
  B: "To Be Processed",
  C: "Closed",
  K: "Cancelled",
  S: "Settled",
};

export const ACTIONABLE_STATUSES = ["N", "T", "B", "P"];

export function getLoggedInUser(): any {
  try {
    const raw = localStorage.getItem("user");
    if (!raw || raw === "undefined") return {};
    const parsed = JSON.parse(raw);
    return parsed?.data?.user || parsed?.user || parsed || {};
  } catch {
    return {};
  }
}

export function getLoggedInEmpId(): string {
  const user = getLoggedInUser();
  return String(
    user?.empId || user?.details?.empId || user?.usrid || user?.emp_id || ""
  ).trim();
}

export function getEmployeeDisplayName(user = getLoggedInUser()): string {
  const details = user?.details || {};
  return (
    [details?.firstname, details?.lsastname || details?.secondname || details?.lastname]
      .filter(Boolean)
      .join(" ")
      .trim() || "-"
  );
}

export function getApproverName(user = getLoggedInUser()): string {
  const details = user?.details || user?.employee || {};
  return (
    [details?.firstname, details?.lsastname || details?.secondname || details?.lastname]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    user?.name ||
    ""
  );
}

export function getEmployeePrefill(user = getLoggedInUser()) {
  const details = user?.details || {};
  return {
    name: getEmployeeDisplayName(user),
    designation: String(details?.sap_department_designation_wings?.designation_name || "").trim(),
    dateOfJoining: formatSapDate(details?.date_of_joining),
    postcode: String(details?.pay_scale || "").trim(),
  };
}

export function unwrap(res: any): any {
  return res?.data ?? res;
}

export function extractResults(data: any): any[] {
  const root = unwrap(data);
  const inner = root?.data ?? root;
  if (Array.isArray(inner?.items)) return inner.items;
  if (Array.isArray(inner?.data?.items)) return inner.data.items;
  if (Array.isArray(inner?.d?.results)) return inner.d.results;
  if (Array.isArray(inner?.results)) return inner.results;
  if (Array.isArray(inner?.data)) return inner.data;
  if (Array.isArray(inner)) return inner;
  if (Array.isArray(root?.items)) return root.items;
  if (Array.isArray(root?.d?.results)) return root.d.results;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root)) return root;
  return [];
}

function firstNonEmpty(...vals: any[]): string {
  for (const v of vals) {
    const s = v == null ? "" : String(v).trim();
    if (s) return s;
  }
  return "";
}

export function extractCodeOptions(data: any): SwfOption[] {
  return extractResults(data)
    .map((item: any) => {
      const code = firstNonEmpty(item.Value, item.value, item.code, item.id, item.Id);
      const description = firstNonEmpty(
        item.Ddtext,
        item.DdText,
        item.ddtext,
        item.description,
        item.label,
        item.Name,
        item.name,
        code
      );
      return { code, description: description || code };
    })
    .filter((o) => o.code);
}

export function resolveSwfPurposeCode(purpose: string, purposes: SwfOption[] = []): string {
  const raw = String(purpose || "").trim();
  if (!raw) return "";
  const known = ["MARG", "CHBG", "NKGG", "SCLG", "FUAS", "LOSI", "HCPD", "DPGG", "NTAB", "CANC", "DCAN"];
  const fromList = purposes.find(
    (p) => String(p.code).trim() === raw || String(p.description).trim() === raw
  );
  const candidate = (fromList?.code || raw).toUpperCase();
  if (known.includes(candidate)) return candidate === "DCAN" ? "CANC" : candidate;

  const d = raw.toLowerCase();
  if (d.includes("tablet")) return "NTAB";
  if (d.includes("marriage")) return "MARG";
  if (d.includes("child birth") || d.includes("childbirth")) return "CHBG";
  if (d.includes("nursery") || d.includes("1st time") || d.includes("first time")) return "NKGG";
  if (d.includes("school")) return "SCLG";
  if (d.includes("funeral")) return "FUAS";
  if (d.includes("cancer") || d.includes("diet allowance")) return "CANC";
  if (d.includes("long sickness") || d.includes("sickness")) return "LOSI";
  if (d.includes("handicapped") || d.includes("disability")) return "HCPD";
  if (d.includes("graduation") || d.includes("degree course") || d.includes("dependent daughter")) {
    return "DPGG";
  }
  return candidate;
}

export function isMarriageClaimFor(
  value: string,
  type: "self" | "son" | "daughter",
  options: SwfOption[] = []
): boolean {
  const v = String(value || "").trim();
  if (!v) return false;
  if (type === "self" && (v === "0" || /^self$/i.test(v))) return true;
  if (type === "son" && (v === "1" || /^son$/i.test(v))) return true;
  if (type === "daughter" && (v === "2" || /^daughter$/i.test(v))) return true;
  const desc = (options.find((o) => String(o.code) === v)?.description || v).toLowerCase();
  if (type === "self") return desc.includes("self");
  if (type === "son") return /\bson\b/.test(desc) && !desc.includes("daughter");
  return desc.includes("daughter");
}

export function extractNameOptions(
  data: any,
  nameKeys: string[]
): { name: string; raw: any }[] {
  return extractResults(data)
    .map((item: any) => {
      let name = "";
      for (const key of nameKeys) {
        if (item?.[key]) {
          name = String(item[key]).trim();
          break;
        }
      }
      if (!name) {
        name = String(
          item.NameOfChild ||
            item.NameOfSon ||
            item.NameOfDaughter ||
            item.NameOfSpouse ||
            item.name ||
            item.childName ||
            item.description ||
            item.Ddtext ||
            item.Value ||
            item.code ||
            ""
        ).trim();
      }
      return { name, raw: item };
    })
    .filter((o) => o.name);
}

export function formatSapDate(value: any): string {
  if (value === undefined || value === null || value === "") return "";
  const str = String(value).trim();
  if (!str || str === "-") return "";
  const msMatch = str.match(/\/Date\((\d+)\)\//);
  if (msMatch) {
    const date = new Date(parseInt(msMatch[1], 10));
    if (isNaN(date.getTime())) return str;
    return toDmy(date);
  }
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const dmy = str.match(/^(\d{2})[-/.](\d{2})[-/.](\d{4})/);
  if (dmy) return `${dmy[1]}-${dmy[2]}-${dmy[3]}`;
  const date = new Date(str);
  if (!isNaN(date.getTime())) return toDmy(date);
  return str;
}

function toDmy(date: Date): string {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${date.getFullYear()}`;
}

export function formatSapDateTime(value: any): string {
  if (!value) return "-";
  const str = String(value).trim();
  const msMatch = str.match(/\/Date\((\d+)\)\//);
  const date = msMatch ? new Date(parseInt(msMatch[1], 10)) : new Date(str);
  if (isNaN(date.getTime())) return str;
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${dd}-${mm}-${date.getFullYear()} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
}

export function getStatusLabel(raw: any, t: (key: string) => string): string {
  const code = String(raw ?? "").trim().toUpperCase();
  if (!code) return "-";
  const key = STATUS_MAP[code];
  if (key) {
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  return STATUS_LABELS[code] || String(raw ?? "").trim() || "-";
}

export function getStatusBadgeClass(raw: any): string {
  const code = String(raw ?? "").trim().toUpperCase();
  if (code === "A" || code === "S" || code === "D") return "badge bg-success";
  if (code === "R" || code === "K") return "badge bg-danger";
  if (code === "T" || code === "P") return "badge bg-warning text-dark";
  if (code === "B") return "badge bg-info text-dark";
  if (code === "N") return "badge bg-primary";
  if (code === "X" || code === "C") return "badge bg-secondary";
  return "badge bg-secondary";
}

export function isSwfActionable(status: any): boolean {
  return ACTIONABLE_STATUSES.includes(String(status || "").trim().toUpperCase());
}

export function isStaffWelfareWorklistItem(task: {
  type?: string;
  taskText?: string;
  workItemText?: string;
  subject?: string;
}): boolean {
  const type = String(task?.type || "").toUpperCase();
  if (type === "SWF" || type === "STAFF_WELFARE" || type.includes("STAFF_WELFARE")) {
    return true;
  }
  const text = `${task?.taskText || ""} ${task?.workItemText || ""} ${task?.subject || ""}`.toLowerCase();
  return (
    text.includes("staff welfare") ||
    text.includes("staff-welfare") ||
    text.includes("swf")
  );
}

export function mapApiItemToCard(apiItem: any) {
  return {
    id: String(apiItem.id ?? apiItem.Id ?? ""),
    referenceNumber: String(apiItem.referenceNumber || apiItem.ReferenceNumber || "").trim(),
    requestStatus: apiItem.requestStatus ?? apiItem.RequestStatus ?? "",
    purpose: apiItem.purpose ?? apiItem.Purpose ?? "",
    claimType: apiItem?.display_meta?.label || apiItem.purpose || "-",
    claimAmount: apiItem.claimAmount ?? apiItem.ClaimAmount ?? "",
    requestCreationDate: formatSapDate(
      apiItem.requestCreationDate || apiItem.RequestCreationDate || apiItem.created_at
    ),
    approverUserID: apiItem.approverUserID || apiItem.approverName || "",
    hRemarks: apiItem.hRemarks || apiItem.remarks || "",
    raw: apiItem,
  };
}

export async function fetchSwfOptions(url: string): Promise<SwfOption[]> {
  const res: any = await Api.post(url, {});
  return extractCodeOptions(res);
}

export async function fetchPurposes(): Promise<SwfOption[]> {
  return fetchSwfOptions(API_URLS.STAFF_WELFARE.PURPOSES);
}

export async function fetchMarriageGiftClaims(): Promise<SwfOption[]> {
  return fetchSwfOptions(API_URLS.STAFF_WELFARE.MARRIAGE_GIFT_CLAIMS);
}

export async function fetchChildNames(): Promise<{ name: string; raw: any }[]> {
  const res: any = await Api.post(API_URLS.STAFF_WELFARE.CHILD_NAMES, {});
  return extractNameOptions(res, ["NameOfChild"]);
}

export async function fetchSonNames(): Promise<{ name: string; raw: any }[]> {
  const res: any = await Api.post(API_URLS.STAFF_WELFARE.SON_NAMES, {});
  return extractNameOptions(res, ["NameOfSon", "NameOfChild"]);
}

export async function fetchDaughterNames(): Promise<{ name: string; raw: any }[]> {
  const res: any = await Api.post(API_URLS.STAFF_WELFARE.DAUGHTER_NAMES, {});
  return extractNameOptions(res, ["NameOfDaughter", "NameOfChild"]);
}

export async function fetchSpouseNames(): Promise<{ name: string; raw: any }[]> {
  const res: any = await Api.post(API_URLS.STAFF_WELFARE.SPOUSE_NAMES, {});
  return extractNameOptions(res, ["NameOfSpouse"]);
}

export async function fetchNoOfChildren(): Promise<SwfOption[]> {
  return fetchSwfOptions(API_URLS.STAFF_WELFARE.NO_OF_CHILDREN);
}

export async function fetchChildClasses(): Promise<SwfOption[]> {
  return fetchSwfOptions(API_URLS.STAFF_WELFARE.CHILD_CLASSES);
}

export async function fetchAdmissionTypes(): Promise<SwfOption[]> {
  return fetchSwfOptions(API_URLS.STAFF_WELFARE.ADMISSION_TYPES);
}

export async function fetchPercentageMarks(): Promise<SwfOption[]> {
  return fetchSwfOptions(API_URLS.STAFF_WELFARE.PERCENTAGE_MARKS);
}

export async function fetchRelationships(): Promise<SwfOption[]> {
  return fetchSwfOptions(API_URLS.STAFF_WELFARE.RELATIONSHIPS);
}

export async function fetchDiseases(): Promise<SwfOption[]> {
  return fetchSwfOptions(API_URLS.STAFF_WELFARE.DISEASES);
}

export async function fetchDisabilityPercentages(): Promise<SwfOption[]> {
  return fetchSwfOptions(API_URLS.STAFF_WELFARE.DISABILITY_PERCENTAGES);
}

export async function fetchClaimQuarters(): Promise<SwfOption[]> {
  return fetchSwfOptions(API_URLS.STAFF_WELFARE.CLAIM_QUARTERS);
}

export async function fetchCourses(): Promise<SwfOption[]> {
  return fetchSwfOptions(API_URLS.STAFF_WELFARE.COURSES);
}

export async function findAllSwf(payload: {
  page?: number;
  limit?: number;
  status?: string;
  startDate?: string;
  endDate?: string;
}): Promise<any[]> {
  const body: any = {
    page: payload.page ?? 1,
    limit: payload.limit ?? 100,
  };
  if (payload.status) body.status = payload.status;
  if (payload.startDate) body.startDate = payload.startDate;
  if (payload.endDate) body.endDate = payload.endDate;

  const res: any = await Api.post(API_URLS.STAFF_WELFARE.ALL, body);
  if (res?.success === false) {
    throw new Error(res?.message || "Failed to load requests");
  }
  return extractResults(res);
}

export async function findOneSwf(payload: { id?: string; referenceNumber?: string }): Promise<any> {
  const body: any = {};
  if (payload.referenceNumber) body.referenceNumber = payload.referenceNumber;
  if (payload.id) body.id = payload.id;
  const res: any = await Api.post(API_URLS.STAFF_WELFARE.DETAILS, body);
  if (res?.success === false) {
    throw new Error(res?.message || "Failed to load details");
  }
  const root = unwrap(res);
  return root?.data?.data ?? root?.data ?? root;
}

export async function fetchSwfFormConfig(purpose: string): Promise<any> {
  if (!purpose) return null;
  const res: any = await Api.post(API_URLS.STAFF_WELFARE.FORM_CONFIG, { purpose });
  if (res?.success === false) {
    throw new Error(res?.message || "Failed to load form config");
  }
  const root = unwrap(res);
  return root?.data ?? root;
}

export async function createSwfRequest(payload: any): Promise<any> {
  return Api.post(API_URLS.STAFF_WELFARE.CREATE, payload);
}

export async function syncSwf(empId: string): Promise<any> {
  return Api.post(API_URLS.STAFF_WELFARE.SYNC, { empId });
}

export async function approveRejectSwf(payload: {
  ReferenceNumber: string;
  Approve_Reject_flag: "A" | "R";
  hRemarks: string;
  approverName: string;
}): Promise<any> {
  return Api.post(API_URLS.STAFF_WELFARE.APPROVE_REJECT, payload);
}

export function isSwfSubmitSuccess(res: any): boolean {
  if (!res) return false;
  const nested = res?.data;
  if (res.success === false) return false;
  if (nested && typeof nested === "object" && !Array.isArray(nested) && nested.success === false) {
    return false;
  }
  const status = Number(res.status ?? res.statusCode ?? nested?.status ?? 0);
  if (status >= 200 && status < 300) return true;
  if (res.success === true || nested?.success === true) return true;
  const msg = String(res.message || nested?.message || "").toLowerCase();
  if (msg.includes("success")) return true;
  return true;
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function getFileExtension(fileName?: string, mimeType?: string): string {
  if (fileName) {
    const ext = fileName.split(".").pop()?.toLowerCase();
    if (ext) return ext === "jpeg" ? "jpg" : ext;
  }
  const mt = String(mimeType || "").toLowerCase();
  if (mt.includes("pdf")) return "pdf";
  if (mt.includes("jpeg") || mt.includes("jpg")) return "jpg";
  if (mt.includes("png")) return "png";
  return "pdf";
}

export function getDocumentTypeForPurpose(purpose: string, purposes: SwfOption[] = []): string {
  switch (resolveSwfPurposeCode(purpose, purposes)) {
    case "MARG":
      return "Marriage Certificate";
    case "CHBG":
      return "Birth Certificate";
    case "NKGG":
      return "Admission Receipt";
    case "SCLG":
    case "DPGG":
      return "Passing Marksheet";
    case "FUAS":
      return "Death Certificate";
    case "HCPD":
      return "Disability Certificate";
    case "LOSI":
    case "CANC":
      return "Medical Certificate";
    case "NTAB":
      return "Support Document";
    default:
      return "Support Document";
  }
}

export async function uploadSwfFile(file: File): Promise<{
  fileName: string;
  fileSize: string;
  fileExtension: string;
  typeOfAttachedDoc?: string;
}> {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) {
    throw new Error(`"${file.name}": Only PDF and image files are supported.`);
  }
  const mimeType =
    ext === "pdf" ? "application/pdf" : ext === "png" ? "image/png" : "image/jpeg";
  const filestring = await fileToBase64(file);
  const res: any = await Api.post(API_URLS.AWS_BUCKET.UPLOAD_FILE, {
    filestring,
    mime_type: file.type && /^[a-z]+\//i.test(file.type) ? file.type : mimeType,
    module: "StaffWelfareFundGrants",
  });
  const data = unwrap(res);
  const key = data?.key || data?.data?.key;
  if (!key) {
    throw new Error(res?.message || data?.message || "Upload failed");
  }
  return {
    fileName: key,
    fileSize: String(data.fileSize || data?.data?.fileSize || `${(file.size / 1024).toFixed(2)} KB`),
    fileExtension: String(data.fileExtension || data?.data?.fileExtension || getFileExtension(file.name, file.type)),
  };
}

function pickDocUrl(obj: any): string {
  if (!obj || typeof obj !== "object") return "";
  return String(
    obj.fileUrl ||
      obj.FileUrl ||
      obj.url ||
      obj.Url ||
      obj.URL ||
      obj.signedUrl ||
      obj.downloadUrl ||
      ""
  ).trim();
}

function pickDocContent(obj: any): string {
  if (!obj || typeof obj !== "object") return "";
  return String(obj.Content || obj.content || obj.FileContent || obj.fileContent || "").trim();
}

function base64ToObjectUrl(base64: string, mimeType = "application/pdf"): string {
  let cleaned = base64.trim();
  const commaIdx = cleaned.indexOf("base64,");
  if (commaIdx !== -1) cleaned = cleaned.slice(commaIdx + "base64,".length);
  cleaned = cleaned.replace(/\s/g, "");
  const byteChars = atob(cleaned);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    bytes[i] = byteChars.charCodeAt(i);
  }
  return URL.createObjectURL(new Blob([bytes], { type: mimeType }));
}

export function getSwfDocuments(detail: any): any[] {
  const docs = detail?.documents || detail?.ToDocument?.results || [];
  return Array.isArray(docs) ? docs : [];
}

export function getSwfRemarks(detail: any): any[] {
  if (Array.isArray(detail?.previous_remarks)) return detail.previous_remarks;
  if (Array.isArray(detail?.remarks_data)) return detail.remarks_data;
  return [];
}

export async function openSwfDocument(document: any): Promise<void> {
  const direct = pickDocUrl(document);
  if (direct) {
    window.open(direct, "_blank", "noopener,noreferrer");
    return;
  }
  const docId = document?.attachmentId || document?.id || document?.docId;
  if (!docId) throw new Error("Document id not found");
  const res: any = await Api.post(API_URLS.STAFF_WELFARE.DOCUMENT, { docId });
  const root = unwrap(res);
  const item =
    root?.d?.results?.[0] ||
    root?.data?.d?.results?.[0] ||
    (Array.isArray(root?.data) ? root.data[0] : null) ||
    root?.data ||
    root;
  const url = pickDocUrl(item);
  const content = pickDocContent(item);
  const fileName = String(item?.FileName || item?.fileName || document?.fileName || "document").trim();
  const mime =
    String(item?.FileType || item?.mimeType || "").trim() ||
    (fileName.toLowerCase().endsWith(".pdf") ? "application/pdf" : "application/octet-stream");
  let viewUrl = url;
  if (!viewUrl && content) {
    viewUrl = /^https?:\/\//i.test(content) ? content : base64ToObjectUrl(content, mime);
  }
  if (!viewUrl) throw new Error(res?.message || "Failed to open document");
  window.open(viewUrl, "_blank", "noopener,noreferrer");
}

export function isSwfFieldVisible(field: any, data: any): boolean {
  if (!field?.visibleWhen) return true;
  const conditionValue = data?.[field.visibleWhen.field];
  if (field.visibleWhen.is && conditionValue !== field.visibleWhen.is) return false;
  if (field.visibleWhen.in && !field.visibleWhen.in.includes(conditionValue)) return false;
  return true;
}

export function formatSwfFieldDisplay(field: any, data: any, purpose?: string): { label: string; value: string; type: string } {
  let value = data?.[field.key];
  let label = field.label || field.key;
  if (field.key === "nameOfChild" && !value) {
    value = data?.nameOfPerson;
  }
  const purposeCode = purpose || data?.purpose;
  const isFuneral = purposeCode === "FUAS" || purposeCode === "Funeral Assistance";
  if (isFuneral) {
    if (field.key === "dateOfIncident") label = "Date of expiry";
    else if (field.key === "nameOfPerson") label = "Name of person expired";
  }
  if (field.type === "date" && value) {
    value = formatSapDate(value) || value;
  } else if (field.key === "marriageClaimFor") {
    if (value === "0") value = "Self";
    else if (value === "1") value = "Son";
    else if (value === "2") value = "Daughter";
  } else if (field.key === "disabilityAbove40") {
    if (value === "Y" || value === "y") value = "YES";
    else if (value === "N" || value === "n") value = "NO";
  } else if (field.options) {
    const option = field.options.find((opt: any) => String(opt.value ?? opt.code) === String(value));
    if (option) value = option.label || option.description || value;
  }
  return {
    label,
    value: value === undefined || value === null || value === "" ? "" : String(value),
    type: field.type || "text",
  };
}

export function getYearOptions(count = 100): SwfOption[] {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: count }, (_, i) => {
    const y = String(currentYear - i);
    return { code: y, description: y };
  });
}

export function getPassedInYearOptions(): SwfOption[] {
  const currentYear = new Date().getFullYear();
  return [
    { code: String(currentYear), description: String(currentYear) },
    { code: String(currentYear - 1), description: String(currentYear - 1) },
  ];
}

export function getMonthOptions(): SwfOption[] {
  return Array.from({ length: 12 }, (_, i) => ({
    code: String(i),
    description: new Date(2000, i, 1).toLocaleString("default", { month: "long" }),
  }));
}

export function buildSwfCreatePayload(form: any, empId: string, purposes: SwfOption[] = []): any {
  const purpose = resolveSwfPurposeCode(form.purpose, purposes) || form.purpose;
  const payload: any = {
    purpose,
    remarks: `Staff welfare fund grant request for ${purpose}`,
    is_draft: false,
    department: form.department,
    placeOfPosting: form.placeOfPosting,
    documents: (form.documents || []).map((doc: any) => ({
      fileName: doc.fileName,
      fileExtension: doc.fileExtension,
      fileSize: doc.fileSize,
      typeOfAttachedDoc: doc.typeOfAttachedDoc,
      documentUploadedBy: doc.documentUploadedBy || empId,
    })),
  };

  if (purpose === "MARG") {
    if (form.dateOfMarriage) payload.dateOfIncident = form.dateOfMarriage;
    payload.nameOfPerson = form.nameOfSpouse || form.name;
    const claimForChild =
      isMarriageClaimFor(form.marriageClaimFor, "son") ||
      isMarriageClaimFor(form.marriageClaimFor, "daughter");
    if (claimForChild) payload.nameOfChild = form.nameOfChild;
    if (form.marriageClaimFor) payload.marriageClaimFor = form.marriageClaimFor;
    if (isMarriageClaimFor(form.marriageClaimFor, "self")) payload.relation = "SELF";
    else if (isMarriageClaimFor(form.marriageClaimFor, "son")) payload.relation = "SON";
    else if (isMarriageClaimFor(form.marriageClaimFor, "daughter")) payload.relation = "DAUGHTER";
  } else if (purpose === "CHBG") {
    if (form.dateOfChildBirth) payload.dateOfIncident = form.dateOfChildBirth;
    if (form.nameOfChild) payload.nameOfPerson = form.nameOfChild;
    if (form.childOrder) payload.noOfChild = form.childOrder;
    if (form.spouseDeclaration) payload.check = "X";
  } else if (purpose === "NKGG") {
    if (form.dateOfFirstAdmission) payload.dateOfIncident = form.dateOfFirstAdmission;
    if (form.nameOfChild) payload.nameOfPerson = form.nameOfChild;
    if (form.admissionClass) payload.Class = form.admissionClass;
  } else if (purpose === "SCLG") {
    if (form.dateOfResultDeclaration) payload.dateOfIncident = form.dateOfResultDeclaration;
    if (form.nameOfChild) payload.nameOfPerson = form.nameOfChild;
    if (form.class) payload.Class = form.class;
    if (form.percentageOfMarks) payload.percentageOfMarks = form.percentageOfMarks;
  } else if (purpose === "FUAS") {
    if (form.dateOfExpiry) payload.dateOfIncident = form.dateOfExpiry;
    if (form.nameOfPersonExpired) payload.nameOfPerson = form.nameOfPersonExpired;
    if (form.relationship) payload.relationship = form.relationship;
  } else if (purpose === "LOSI" || purpose === "CANC") {
    if (form.disease) payload.disease = form.disease;
    if (form.claimForTheYear) payload.claimForTheYear = form.claimForTheYear;
    if (form.claimForTheMonth) payload.claimForTheMonth = form.claimForTheMonth;
  } else if (purpose === "HCPD") {
    if (form.nameOfChild) payload.nameOfPerson = form.nameOfChild;
    if (form.claimForTheYear) payload.claimForTheYear = form.claimForTheYear;
    if (form.claimForTheQuarter) payload.claimForTheQuarter = form.claimForTheQuarter;
    if (form.disabilityAbove40) payload.disabilityAbove40 = form.disabilityAbove40;
    if (form.disabilityDetails) payload.disabilityDetails = form.disabilityDetails;
  } else if (purpose === "DPGG") {
    if (form.dateOfResultDeclaration) payload.dateOfIncident = form.dateOfResultDeclaration;
    if (form.nameOfChild) payload.nameOfPerson = form.nameOfChild;
    if (form.course) payload.Class = form.course;
    if (form.courseName) payload.courseName = form.courseName;
    if (form.aggregateMarks) payload.aggregateMarks = form.aggregateMarks;
    if (form.passedInTheYear) payload.passedInTheYear = form.passedInTheYear;
  } else if (purpose === "NTAB") {
    if (form.nameOfChild) payload.nameOfPerson = form.nameOfChild;
    if (form.aggregateMarks) payload.aggregateMarks = form.aggregateMarks;
    if (form.durationOfThePGCourse) payload.durationOfThePGCourse = form.durationOfThePGCourse;
    if (form.nameOfThePGCourseInWhichAdmission) {
      payload.nameOfThePGCourseInWhichAdmission = form.nameOfThePGCourseInWhichAdmission;
    }
    if (form.nameOfCollegeOrUniversity) payload.nameOfCollegeOrUniversity = form.nameOfCollegeOrUniversity;
    if (form.graduationCourse) payload.graduationCourse = form.graduationCourse;
    if (form.collegeOrUniversity) payload.collegeOrUniversity = form.collegeOrUniversity;
    if (form.dateOfAdmission) payload.dateOfAdmission = form.dateOfAdmission;
    if (form.purchaseAmountOfTablet) payload.purchaseAmountOfTablet = form.purchaseAmountOfTablet;
    if (form.dateOfPurchaseOfTablet) payload.dateOfIncident = form.dateOfPurchaseOfTablet;
  }

  return payload;
}
